import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Car, Users, UserCircle, Calendar, MapPin,
  Fuel, Receipt, BarChart3, CreditCard, Settings, LogOut, Bell, Search, AlertCircle,
} from "lucide-react";
import { formatDate } from "@/lib/format";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-owner-dashboard" },
  { to: "/app/bookings", label: "Bookings", icon: Calendar, testid: "nav-owner-bookings" },
  { to: "/app/trips", label: "Trip Sheets", icon: MapPin, testid: "nav-owner-trips" },
  { to: "/app/vehicles", label: "Vehicles", icon: Car, testid: "nav-owner-vehicles" },
  { to: "/app/drivers", label: "Drivers", icon: UserCircle, testid: "nav-owner-drivers" },
  { to: "/app/clients", label: "Clients", icon: Users, testid: "nav-owner-clients" },
  { to: "/app/fuel", label: "Fuel", icon: Fuel, testid: "nav-owner-fuel" },
  { to: "/app/expenses", label: "Expenses", icon: Receipt, testid: "nav-owner-expenses" },
  { to: "/app/reports", label: "Reports", icon: BarChart3, testid: "nav-owner-reports" },
  { to: "/app/billing", label: "Subscription", icon: CreditCard, testid: "nav-owner-billing" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-owner-settings" },
];

export default function OwnerShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const tenant = user?.tenant;
  const trialActive = tenant?.status === "trial";

  return (
    <div data-testid="owner-shell" className="flex min-h-screen bg-zinc-100">
      <aside className="w-64 bg-asphalt text-white flex flex-col">
        <div className="px-5 h-16 flex items-center gap-2 border-b border-zinc-800">
          <span className="h-8 w-8 bg-cab flex items-center justify-center rounded font-display font-black text-asphalt">W</span>
          <div>
            <div className="font-display font-bold text-sm truncate max-w-[160px]">{tenant?.company_name || "WebCabz"}</div>
            <div className="text-[10px] uppercase tracking-widest text-cab">{tenant?.plan_name || "Trial"}</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-auto">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
              <Icon /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <button
            data-testid="owner-logout"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="sidebar-link w-full"
          >
            <LogOut /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col">
        <header className="h-16 bg-white border-b border-zinc-200 px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-zinc-500">
            <Search className="h-4 w-4" />
            <input placeholder="Search bookings, vehicles, drivers…" className="bg-transparent text-sm outline-none w-80" />
          </div>
          <div className="flex items-center gap-4">
            {trialActive && (
              <div className="hidden md:flex items-center gap-2 text-xs px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-md">
                <AlertCircle className="h-3.5 w-3.5" /> Trial expires {formatDate(tenant?.expiry_date)} ·{" "}
                <NavLink to="/app/billing" className="font-semibold underline">Upgrade</NavLink>
              </div>
            )}
            <button className="text-zinc-500 relative">
              <Bell className="h-4 w-4" />
              <span className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-cab" />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-asphalt text-cab flex items-center justify-center font-display font-bold text-sm">
                {(user?.name || "U").charAt(0)}
              </div>
              <div className="text-xs">
                <div className="font-semibold text-asphalt">{user?.name}</div>
                <div className="text-zinc-500 capitalize">{user?.role?.replace("_", " ")}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
